import React, { Component } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import MaterialIcon from 'material-icons-react';
import { withTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { renewPersonErrorStrings, renewPersonRules, errorStrings } from 'shared/validations';
import * as tcRenewalAction from 'features/tc-renewal/duck';
import { ValidationService } from 'shared/services';
import { AlertNotice, SimpleModal, ModalInfo } from 'shared/components';
import isEmpty from 'lodash/isEmpty';
import * as helper from 'features/tc-renewal/tc-secure-storage/helper';
import PersonForm from './PersonForm';
import './AddPerson.scss';

const defaultUser = {
  firstName: '',
  lastName: '',
  phone: '',
  emailAddress: '',
};

const currentIsAlternate = (currentPers, alternate) =>
  currentPers.firstName === alternate.firstName && currentPers.lastName === alternate.lastName;

const disableAlternate = (listOfPers, alternate) =>
  !!listOfPers.find((pers) => currentIsAlternate(pers, alternate));

class AddPerson extends Component {
  constructor(props) {
    super(props);

    let disabledAlternate = false;
    if (props.listOfPersons.length) {
      disabledAlternate = disableAlternate(props.listOfPersons, props.alternateContact);
    }

    this.state = {
      modalIsOpen: false,
      confirmModalOpen: false,
      currentPerson: props.user || defaultUser,
      listOfPersons: props.listOfPersons,
      checkedAlternate: false,
      disabledAlternate,
      isDirty: false,
      errorUpdate: '',
      errors: {},
    };

    this.toggleModal = this.toggleModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPhoneChange = this.onPhoneChange.bind(this);
    this.setAlternateContact = this.setAlternateContact.bind(this);
    this.addPerson = this.addPerson.bind(this);
    this.removePerson = this.removePerson.bind(this);
    this.closeConfirmModal = this.closeConfirmModal.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.listOfPersons !== this.props.listOfPersons) {
      this.setState({
        listOfPersons: this.props.listOfPersons,
      });
    }
  }

  onChange(e) {
    let newState = { ...this.state };
    const { name, value } = e.target;
    delete this.state.errors[name];
    newState = {
      currentPerson: {
        ...this.state.currentPerson,
        [name]: value,
      },
    };
    const errors = ValidationService.validate(
      newState.currentPerson,
      helper.onTypeRules,
      errorStrings
    );

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    }

    this.setState({
      ...newState,
      isDirty: true,
    });
  }

  onPhoneChange(e) {
    const { name, rawValue } = e.target;

    delete this.state.errors[name];
    this.setState((prevState) => ({
      currentPerson: {
        ...prevState.currentPerson,
        [name]: rawValue,
      },
      isDirty: true,
    }));
  }

  setAlternateContact(e) {
    const { checked } = e.target;
    const { currentPerson, prevPerson } = this.state;

    const person = checked ? { ...this.props.alternateContact } : prevPerson || { ...defaultUser };
    if (currentPerson) {
      person.id = currentPerson.id;
      person.testCenterCycle = currentPerson.testCenterCycle;
    }

    this.setState({
      checkedAlternate: checked,
      prevPerson: checked ? currentPerson : null,
      currentPerson: person,
      isDirty: true,
    });
  }

  toggleModal(person) {
    const { modalIsOpen } = this.state;
    const { listOfPersons, alternateContact } = this.props;

    if (!modalIsOpen && person) {
      const checkedAlternate = currentIsAlternate(person, this.props.alternateContact);

      this.setState((prevState) => ({
        checkedAlternate,
        disabledAlternate: checkedAlternate
          ? false
          : disableAlternate(listOfPersons, alternateContact),
        modalIsOpen: !prevState.modalIsOpen,
        currentPerson: person || defaultUser,
        prevPerson: null,
      }));
      return;
    }

    this.setState((prevState) => ({
      modalIsOpen: !prevState.modalIsOpen,
      currentPerson: person || defaultUser,
      prevPerson: null,
      disabledAlternate: disableAlternate(listOfPersons, alternateContact),
    }));
  }

  closeModal(confirmation) {
    if (confirmation && this.state.isDirty) {
      this.setState({
        confirmModalOpen: true,
      });
    } else {
      this.setState({
        modalIsOpen: false,
        confirmModalOpen: false,
        checkedAlternate: false,
        prevPerson: null,
        currentPerson: defaultUser,
        isDirty: false,
        errorUpdate: '',
        errors: {},
      });
    }
  }

  addPerson() {
    const { contactTypeId } = this.props;
    const { currentPerson, listOfPersons } = this.state;
    const list = [...listOfPersons];

    const errors = ValidationService.validate(
      currentPerson,
      renewPersonRules,
      renewPersonErrorStrings
    );

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    }
    currentPerson.contactType = {
      id: contactTypeId,
    };

    if (currentPerson.id) {
      const idx = list.findIndex((p) => p.id === currentPerson.id);
      list[idx] = currentPerson;
    } else {
      list.push(currentPerson);
    }

    this.props.updateContactsList(currentPerson, list).then(() => {
      if (isEmpty(this.props.errorSetContact)) {
        this.setState({
          listOfPersons: list,
        });
        this.closeModal(false);
        return;
      }
      this.setState({
        errorUpdate: this.props.errorSetContact.message,
      });
    });
  }

  removePerson(person) {
    const listOfPersons = [...this.state.listOfPersons];

    const idx = listOfPersons.findIndex((p) => p.id === person.id);
    listOfPersons.splice(idx, 1);

    this.props.updateContactsList(person, listOfPersons, true).then(() => {
      if (!isEmpty(this.props.errorRemovingContact)) {
        this.setState({
          errorRemoving: '',
        });
      }
    });
  }

  closeConfirmModal() {
    this.setState({
      confirmModalOpen: false,
    });
  }

  render() {
    const { className, labels, error, hideAddButton, personRequired } = this.props;
    const {
      modalIsOpen,
      currentPerson,
      checkedAlternate,
      listOfPersons,
      disabledAlternate,
      confirmModalOpen,
      errorUpdate,
      errors,
    } = this.state;

    const modalHeader = currentPerson.id
      ? labels.editSaveLabels.modalEditTitle
      : labels.editSaveLabels.modalAddTitle;
    const modalButton = currentPerson.id
      ? labels.editSaveLabels.modalSaveButton
      : labels.editSaveLabels.modalAddButton;

    return (
      <div className={`add-person-container ${className}`}>
        <h6 className="fw-bold mb-0">{this.props.t(labels.sectionTitle)}</h6>
        {error && error.length && (
          <span className="text-error" key={error[0]}>
            {this.props.t(error[0])}
          </span>
        )}
        <div className="mt-3">
          {listOfPersons.length
            ? listOfPersons.map((person) => (
                <div className="list-row" key={uniqueId('person-elem-')}>
                  <div className="">
                    <p className="fw-bold mb-0">
                      {person.lastName}, {person.firstName}
                    </p>
                    <p className="mb-0">{person.emailAddress}</p>
                  </div>
                  <div className="person-buttons-container">
                    <button
                      className="btn-link text-pacific-blue text-strong"
                      onClick={() => this.toggleModal(person)}>
                      <span className="fw-bold">
                        <i className="fa fa-pencil" />
                        {this.props.t('testCenter.storage.editPersonLabel')}
                      </span>
                    </button>
                    <button
                      className="btn-link text-pacific-blue text-strong"
                      onClick={() => this.removePerson(person)}>
                      <span className="fw-bold">
                        <i className="fa fa-trash" />
                        {this.props.t('common.labels.remove')}
                      </span>
                    </button>
                  </div>
                </div>
              ))
            : personRequired && (
                <AlertNotice message={this.props.t('testCenter.storage.noPersonsWarning')} />
              )}
        </div>
        {hideAddButton !== true && (
          <button
            className="pl-0 btn-link text-pacific-blue text-strong add-button"
            onClick={() => this.toggleModal()}>
            <span className="fw-bold">
              <MaterialIcon icon="control_point" size="tiny" />
              {this.props.t(labels.buttonLabel)}
            </span>
          </button>
        )}
        <ModalInfo
          modalIsOpen={modalIsOpen}
          closeModal={() => this.closeModal(true)}
          headerTitle={modalHeader}
          className="additional-person-modal"
          saveLabel={modalButton}
          onSubmit={this.addPerson}>
          <h6 className="modal-message fw-bold">{this.props.t(labels.modalMessage)}</h6>
          <PersonForm
            currentPerson={currentPerson}
            onChange={this.onChange}
            onPhoneChange={this.onPhoneChange}
            setAlternateContact={this.setAlternateContact}
            checkedAlternate={checkedAlternate}
            disabledAlternate={disabledAlternate}
            errorUpdate={errorUpdate}
            errors={errors}
          />
        </ModalInfo>
        <SimpleModal
          isOpen={confirmModalOpen}
          onCancel={this.closeConfirmModal}
          onConfirm={() => this.closeModal(false)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('staffManagement.discardChangesTitle')}
          message={this.props.t('testCenter.backModal.message')}
          buttonsReverseOrder
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    errorSetContact: state.tcRenewal.errorSetContact,
    errorRemovingContact: state.tcRenewal.errorRemovingContact,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(tcRenewalAction, dispatch),
  };
}

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
  })(AddPerson)
);

AddPerson.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({}),
  alternateContact: PropTypes.shape({}).isRequired,
  listOfPersons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  error: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  labels: PropTypes.shape({}).isRequired,
  updateContactsList: PropTypes.func.isRequired,
  contactTypeId: PropTypes.number.isRequired,
  personRequired: PropTypes.bool,
  hideAddButton: PropTypes.bool,
  errorSetContact: PropTypes.bool,
  errorRemovingContact: PropTypes.bool,
  actions: PropTypes.shape({
    setContact: PropTypes.func,
    removeContact: PropTypes.func,
  }),
};

AddPerson.defaultProps = {
  className: '',
  personRequired: false,
  hideAddButton: false,
};
